"use client";

import type { ReactNode } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import type { Platform } from "@/payload-types";
import { CTAButtonLink } from "@/components/shared/CTAButtonLink";
import { cn } from "@/lib/utils";
import { getImageData } from "@/lib/media-utils";
import { GridGroupShell } from "@/payload/blocks/grid-sections/GridGroupShell";
import { GridSectionCrosshairs } from "@/payload/blocks/grid-sections/GridSectionCrosshairs";

const LINE_CSS_COLOR = "color-mix(in oklab, white 15%, transparent)";

interface PlatformContentProps {
  platform: Platform;
  missionGrid?: ReactNode;
}

type PlatformGridLayout = "grid-equal-3" | "grid-full";

interface PlatformRow {
  key: string;
  layout: PlatformGridLayout;
  elevated?: boolean;
  render: (withTopBorder: boolean) => ReactNode;
}

const GRID_LINE_COLOR = "border-white/15";
const GRID_GLOW_COLOR = "border-white/12";

export function PlatformContent({ platform, missionGrid }: PlatformContentProps) {
  const heroData = getImageData(platform.render3d || platform.heroImage, platform.name);
  const belowRenderData = getImageData(platform.imageBelowRender3d, platform.name);
  const belowStatsData = getImageData(platform.imageBelowStats, platform.name);
  const belowMissionData = getImageData(platform.imageBelowMissionGrid, platform.name);
  const diagramSideData = getImageData(platform.diagramSide, "Vessel diagram");
  const diagramTopData = getImageData(platform.diagramTop, "Top view");
  const logoData = getImageData(platform.logo, `${platform.name} logo`);

  const rows: PlatformRow[] = [];

  if (heroData.url) {
    const heroUrl = heroData.url;
    rows.push({
      key: "hero",
      layout: "grid-full",
      elevated: true,
      render: (withTopBorder) => (
        <PlatformGridRow key="hero" layout="grid-full" withTopBorder={withTopBorder} elevated>
          <div className="grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square lg:col-span-3 lg:aspect-[3/1] 2xl:aspect-[4/1]">
            <div className="decorative-grid-cell relative h-full">
              <div className="pointer-events-none absolute inset-x-0 -top-[15%] -bottom-[15%] lg:inset-x-[10%] z-30">
                <Image
                  src={heroUrl}
                  alt={heroData.alt}
                  fill
                  className="object-contain object-center"
                  sizes="100vw"
                  priority
                />
              </div>
            </div>
          </div>
        </PlatformGridRow>
      ),
    });
  }

  if (belowRenderData.url) {
    const belowRenderUrl = belowRenderData.url;
    rows.push({
      key: "below-render",
      layout: "grid-full",
      render: (withTopBorder) => (
        <PlatformGridRow key="below-render" layout="grid-full" withTopBorder={withTopBorder}>
          <PlatformFullSpan>
            <PlatformImageCell>
              <Image
                src={belowRenderUrl}
                alt={belowRenderData.alt}
                fill
                className="object-cover object-center"
                sizes="100vw"
              />
            </PlatformImageCell>
          </PlatformFullSpan>
        </PlatformGridRow>
      ),
    });
  }

  if (
    (platform.specs && platform.specs.length > 0) ||
    (platform.capabilities && platform.capabilities.length > 0) ||
    logoData.url ||
    platform.description
  ) {
    rows.push({
      key: "specs",
      layout: "grid-equal-3",
      render: (withTopBorder) => (
        <PlatformGridRow key="specs" layout="grid-equal-3" withTopBorder={withTopBorder}>
          <PlatformGridCell aspect="">
            <PlatformTextCell>
              <h3 className="font-subheading text-2xl lg:text-3xl 2xl:text-4xl text-white">
                Platform
              </h3>
              {platform.specs && platform.specs.length > 0 && (
                <dl className="mt-6 w-full text-base 2xl:text-lg divide-y divide-white/50">
                  {platform.specs.map((spec, i) => (
                    <div key={i} className="flex w-full items-baseline justify-between py-2.5">
                      <dt className="text-white">{spec.label}</dt>
                      <dd className="text-right font-medium text-white">{spec.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </PlatformTextCell>
          </PlatformGridCell>

          <PlatformGridCell aspect="">
            <div className="decorative-grid-cell relative flex h-full flex-col items-start justify-start gap-6 p-3 sm:p-4 lg:p-5">
              {logoData.url && (
                <Image
                  src={logoData.url}
                  alt={logoData.alt}
                  width={600}
                  height={200}
                  className="h-auto w-auto max-w-[80%] max-h-[25%] object-contain"
                  sizes="(max-width: 1024px) 50vw, 20vw"
                />
              )}
              {platform.description && (
                <p className="text-base lg:text-base 2xl:text-lg leading-relaxed text-white whitespace-pre-line 2xl:max-w-[54ch]">
                  {platform.description}
                </p>
              )}
            </div>
          </PlatformGridCell>

          <PlatformGridCell aspect="">
            <PlatformTextCell>
              <h3 className="font-subheading text-2xl lg:text-3xl 2xl:text-4xl text-white">
                Capabilities
              </h3>
              {platform.capabilities && platform.capabilities.length > 0 && (
                <dl className="mt-6 w-full text-base 2xl:text-lg divide-y divide-white/50">
                  {platform.capabilities.map((cap, i) => (
                    <div key={i} className="flex w-full items-baseline justify-between py-2.5">
                      <dt className="text-white">{cap.category}</dt>
                      <dd className="text-right text-white">{cap.description}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </PlatformTextCell>
          </PlatformGridCell>
        </PlatformGridRow>
      ),
    });
  }

  if (
    (platform.features && platform.features.length > 0) ||
    diagramSideData.url ||
    diagramTopData.url
  ) {
    rows.push({
      key: "features",
      layout: "grid-equal-3",
      render: (withTopBorder) => (
        <PlatformGridRow key="features" layout="grid-equal-3" withTopBorder={withTopBorder}>
          <PlatformGridCell aspect="">
            <PlatformImageCell>
              {diagramSideData.url && (
                <div className="relative w-full h-full flex items-center justify-center p-3 sm:p-4 lg:p-5">
                  <Image
                    src={diagramSideData.url}
                    alt={diagramSideData.alt}
                    width={600}
                    height={400}
                    className="object-contain max-w-[50%] max-h-[50%]"
                    sizes="(max-width: 1024px) 50vw, 16vw"
                  />
                </div>
              )}
            </PlatformImageCell>
          </PlatformGridCell>

          <PlatformGridCell aspect="">
            <PlatformTextCell>
              <h3 className="font-subheading text-2xl lg:text-3xl 2xl:text-4xl text-white">
                Features
              </h3>
              {platform.features && platform.features.length > 0 && (
                <ul className="mt-6 w-full space-y-3 text-base 2xl:text-lg">
                  {platform.features.map((feature, i) => (
                    <li key={i} className="flex w-full items-start gap-3 text-white">
                      <span className="mt-0.5 text-brand">&#9656;</span>
                      <span>{feature.text}</span>
                    </li>
                  ))}
                </ul>
              )}
            </PlatformTextCell>
          </PlatformGridCell>

          <PlatformGridCell aspect="">
            <PlatformImageCell>
              {diagramTopData.url && (
                <div className="relative w-full h-full flex items-center justify-center p-3 sm:p-4 lg:p-5">
                  <Image
                    src={diagramTopData.url}
                    alt={diagramTopData.alt}
                    width={600}
                    height={400}
                    className="object-contain max-w-[50%] max-h-[50%]"
                    sizes="(max-width: 1024px) 50vw, 16vw"
                  />
                </div>
              )}
            </PlatformImageCell>
          </PlatformGridCell>
        </PlatformGridRow>
      ),
    });
  }

  if (belowStatsData.url) {
    const belowStatsUrl = belowStatsData.url;
    rows.push({
      key: "below-stats",
      layout: "grid-full",
      render: (withTopBorder) => (
        <PlatformGridRow key="below-stats" layout="grid-full" withTopBorder={withTopBorder}>
          <PlatformFullSpan>
            <PlatformImageCell>
              <Image
                src={belowStatsUrl}
                alt={belowStatsData.alt}
                fill
                className="object-cover object-center"
                sizes="100vw"
              />
            </PlatformImageCell>
          </PlatformFullSpan>
        </PlatformGridRow>
      ),
    });
  }

  if (platform.actionImages && platform.actionImages.length > 0) {
    platform.actionImages.forEach((item, i) => {
      const imageData = getImageData(item.image, item.caption || platform.name);
      if (!imageData.url) return;
      const imageUrl = imageData.url;

      rows.push({
        key: `action-${i}`,
        layout: "grid-full",
        render: (withTopBorder) => (
          <PlatformGridRow
            key={`action-${i}`}
            layout="grid-full"
            withTopBorder={withTopBorder}
          >
            <PlatformFullSpan>
              <PlatformImageCell>
                <Image
                  src={imageUrl}
                  alt={imageData.alt}
                  fill
                  className="object-cover object-center"
                  sizes="100vw"
                />
              </PlatformImageCell>
            </PlatformFullSpan>
          </PlatformGridRow>
        ),
      });
    });
  }

  // Mission Grid — pre-rendered on the server, just slot it in
  if (missionGrid) {
    rows.push({
      key: "mission-grid",
      layout: "grid-equal-3",
      render: () => <>{missionGrid}</>,
    });
  }

  if (belowMissionData.url) {
    const belowMissionUrl = belowMissionData.url;
    rows.push({
      key: "below-mission",
      layout: "grid-full",
      render: (withTopBorder) => (
        <PlatformGridRow key="below-mission" layout="grid-full" withTopBorder={withTopBorder}>
          <div className="grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square lg:col-span-3 lg:aspect-[3/2] 2xl:aspect-[2/1]">
            <PlatformImageCell>
              <Image
                src={belowMissionUrl}
                alt={belowMissionData.alt}
                fill
                className="object-cover object-center"
                sizes="100vw"
              />
            </PlatformImageCell>
          </div>
        </PlatformGridRow>
      ),
    });
  }

  const brochureUrl =
    platform.brochure &&
    typeof platform.brochure === "object" &&
    "url" in platform.brochure &&
    platform.brochure.url
      ? platform.brochure.url
      : null;

  const hasPrimary = platform.primaryCTA?.label && platform.primaryCTA?.url;
  const hasSecondary = platform.secondaryCTA?.label && platform.secondaryCTA?.url;

  if (hasPrimary || hasSecondary || brochureUrl) {
    rows.push({
      key: "cta",
      layout: "grid-full",
      render: (withTopBorder) => (
        <PlatformGridRow key="cta" layout="grid-full" withTopBorder={withTopBorder}>
          <PlatformFullSpan>
            <PlatformTextCell centered>
              <div className="flex flex-wrap items-center justify-center gap-4">
                {hasPrimary && (
                  <CTAButtonLink
                    label={platform.primaryCTA!.label!}
                    href={platform.primaryCTA!.url!}
                  />
                )}
                {hasSecondary && (
                  <CTAButtonLink
                    label={platform.secondaryCTA!.label!}
                    href={platform.secondaryCTA!.url!}
                  />
                )}
                {brochureUrl && (
                  <CTAButtonLink
                    label="Download Brochure"
                    href={brochureUrl}
                    newTab
                  />
                )}
              </div>
            </PlatformTextCell>
          </PlatformFullSpan>
        </PlatformGridRow>
      ),
    });
  }

  if (!rows.length) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={platform.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <GridGroupShell glowColor={GRID_GLOW_COLOR}>
          {rows.map((row, index) =>
            row.render(index === 0 || (index > 0 && rows[index - 1].elevated === true))
          )}
        </GridGroupShell>
      </motion.div>
    </AnimatePresence>
  );
}

function PlatformGridRow({
  layout,
  withTopBorder,
  elevated = false,
  children,
}: {
  layout: PlatformGridLayout;
  withTopBorder: boolean;
  elevated?: boolean;
  children: ReactNode;
}) {
  void layout; // grid lines now driven by per-cell .grid-cell borders
  return (
    <section
      className={cn(
        "relative w-full text-white border-x grid-decorative-verticals",
        GRID_LINE_COLOR,
        !elevated && "border-b",
        withTopBorder && "border-t",
        elevated ? "overflow-visible" : "z-20"
      )}
      style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
    >
      <div className={cn("relative z-10 grid grid-cols-1 lg:grid-cols-3", elevated && "overflow-visible")}>
        {children}
      </div>
      <GridSectionCrosshairs />
    </section>
  );
}

function PlatformGridCell({
  children,
  className,
  aspect = "aspect-square 2xl:aspect-[4/3]",
}: {
  children: ReactNode;
  className?: string;
  aspect?: string;
}) {
  return (
    <div
      className={cn(
        "grid-cell lg:border-t-0 col-span-1 min-h-48",
        aspect,
        className
      )}
    >
      {children}
    </div>
  );
}

function PlatformFullSpan({ children }: { children: ReactNode }) {
  return (
    <div className="grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square lg:col-span-3 lg:aspect-[3/1] 2xl:aspect-[4/1]">
      {children}
    </div>
  );
}

function PlatformTextCell({
  children,
  centered = false,
}: {
  children: ReactNode;
  centered?: boolean;
}) {
  return (
    <div
      className={cn(
        "decorative-grid-cell relative flex h-full flex-col p-3 sm:p-4 lg:p-5",
        centered
          ? "items-center justify-center text-center"
          : "items-start justify-start text-left"
      )}
    >
      {children}
    </div>
  );
}

function PlatformImageCell({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="decorative-grid-cell relative h-full overflow-hidden">
      {children}
    </div>
  );
}
